import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LogIn, LogOut, KeyRound, RefreshCw, ImagePlus, Shield, AlertCircle } from "lucide-react";

const ICONS = {
  login_success: { i: LogIn, tone: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  login_failed:  { i: AlertCircle, tone: "text-red-600 bg-red-50 border-red-200" },
  password_reset_requested: { i: KeyRound, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  password_reset_completed: { i: KeyRound, tone: "text-[#003DA5] bg-[#F3F7FC] border-[#BFCFE8]" },
  branding_uploaded: { i: ImagePlus, tone: "text-[#003DA5] bg-[#F3F7FC] border-[#BFCFE8]" },
  demo_data_reset: { i: RefreshCw, tone: "text-red-600 bg-red-50 border-red-200" },
};

const LABEL = {
  login_success: "Login",
  login_failed: "Failed login attempt",
  password_reset_requested: "Password reset requested",
  password_reset_completed: "Password reset completed",
  branding_uploaded: "Branding uploaded",
  demo_data_reset: "Demo data reset",
};

const ACTIONS = ["", ...Object.keys(LABEL)];

const shortDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function AdminActivity() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/admin/activity", { params: action ? { action } : {} })
      .then(r => setRows(r.data))
      .finally(() => setLoading(false));
  }, [action]);

  return (
    <div className="space-y-6" data-testid="activity-log-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Admin Activity Log</h1>
          <p className="text-slate-600 mt-1">Sensitive actions across the CRM. Visible to Super Admin only.</p>
        </div>
        <select value={action} onChange={(e) => setAction(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="activity-filter">
          {ACTIONS.map(a => <option key={a} value={a}>{a ? LABEL[a] : "All actions"}</option>)}
        </select>
      </div>

      {loading ? <div className="text-slate-500">Loading…</div> :
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500"><tr>
              <th className="text-left px-4 py-3 w-32">When</th>
              <th className="text-left px-4 py-3">Action</th>
              <th className="text-left px-4 py-3">Actor</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Details</th>
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const meta = ICONS[r.action] || { i: Shield, tone: "text-slate-600 bg-slate-50 border-slate-200" };
                const Icon = meta.i;
                return (
                  <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`activity-row-${r.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500">{shortDT(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-semibold border ${meta.tone}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {LABEL[r.action] || r.action}
                      </span>
                      {r.target && <span className="ml-2 text-xs text-slate-500">→ {r.target}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.actor_name || r.actor_email || "—"}</div>
                      <div className="text-xs text-slate-500">{r.actor_email}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-600">
                      {r.details && Object.keys(r.details).length > 0 ? JSON.stringify(r.details) : ""}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan="4" className="px-4 py-16 text-center text-slate-500">No activity yet.</td></tr>}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}
